/**
 * evaluationGuideContent — B-2026-0057, B-2026-0086, B-2026-0088
 *
 * Static registry mapping context keys to Adaptive Guide content objects.
 * All content is sport-agnostic and written for an athlete audience —
 * plain language, no jargon.
 *
 * B-2026-0086 changes:
 * - `GuideEntry.body` extended to allow `string | ((eval: EvalSummary) => string)`
 *   so high-value entries can reference the athlete's ACTUAL data.
 * - `SECTION_TO_KEY` map links `data-guide-section` attribute values from
 *   EvaluationResults.tsx to registry keys.
 *
 * B-2026-0088 changes:
 * - All body strings migrated to `StructuredGuideBody` (lead + points + lookFor).
 * - Dynamic resolvers now return `StructuredGuideBody`.
 * - `resolveGuideBody` re-exported from `guideContentTypes` for backward compat.
 * - Legacy `string` body type retained for `default` fallback so existing tests pass.
 */

// ---------------------------------------------------------------------------
// Imports + backward-compat re-exports
// ---------------------------------------------------------------------------

import type { EvalSummary, GuideBodyValue, StructuredGuideBody } from './guideContentTypes';
export type { EvalSummary, StructuredGuideBody } from './guideContentTypes';
export { resolveGuideBody } from './guideContentTypes';

import { buildScoringAspectsBody } from './scoringAspects';

export interface GuideEntry {
  heading: string;
  /** Static text, structured body, or a function that receives a minimal eval summary. */
  body: GuideBodyValue;
  learnMoreUrl?: string;
}

// ---------------------------------------------------------------------------
// Key type
// ---------------------------------------------------------------------------

export type EvalGuideKey =
  | 'overall-score'
  | 'body-form-score'
  | 'goals-score'
  | 'eom-score'
  | 'phase-selected'
  | 'region-selected'
  | 'view-mode-2d'
  | 'view-mode-3d'
  | 'coaching-narrative'
  | 'video-analysis'
  | 'goal-achievements'
  | 'goal-score'
  | 'gemini-visual'
  | 'movement-analysis'
  | 'technical-diagnostics'
  | 'share-card'
  | 'comments'
  | 'auto-fail'
  | 'mirroring-badge'
  | 'what-is-measured'
  | 'default';

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const EVAL_GUIDE_CONTENT: Record<EvalGuideKey, GuideEntry> = {
  // Registry-driven: content comes from schemas/scoring_aspects.json, whose
  // claims are pinned to live scoring config by backend stitch tests. Do not
  // hand-write aspect copy here — fix the registry.
  'what-is-measured': {
    heading: 'What Gets Measured',
    body: (): StructuredGuideBody => buildScoringAspectsBody('athlete'),
  },
  'overall-score': {
    heading: 'Your Overall Score',
    body: (eval_: EvalSummary): StructuredGuideBody => {
      const score = eval_.overallScore != null ? Math.round(eval_.overallScore) : null;

      const phases = eval_.phases ?? [];
      let lowestPhase: { label: string; score: number } | null = null;
      for (const p of phases) {
        const s = p.form_score ?? p.spatial_score;
        const label = p.phase_label ?? p.phase_name ?? 'a phase';
        if (s != null) {
          const pct = s > 1 ? s : s * 100;
          if (!lowestPhase || pct < lowestPhase.score) {
            lowestPhase = { label, score: Math.round(pct) };
          }
        }
      }

      const lead =
        score != null
          ? score >= 80
            ? `Your score is ${score}/100 — excellent range, close to the reference.`
            : score >= 60
            ? `Your score is ${score}/100 — solid technique with room to refine.`
            : `Your score is ${score}/100 — one or two focused areas will drive big gains.`
          : 'This combines body shape, goal checkpoints, and movement efficiency.';

      return {
        lead,
        points: [
          'Combines body shape accuracy, goal checkpoints, and movement efficiency.',
          lowestPhase
            ? `"${lowestPhase.label}" is your lowest phase at ${lowestPhase.score} — work there first.`
            : 'Click the lowest-scoring phase tab to isolate where to focus.',
          'Small, targeted corrections typically produce the biggest overall gains.',
        ],
        lookFor: 'The score ring at the top of this card.',
      };
    },
  },

  'body-form-score': {
    heading: 'Body Form Score',
    body: {
      lead: 'How well your joint angles and bone directions matched the reference frame-by-frame.',
      points: [
        'Compares posture, alignment, and joint positions across your whole body.',
        'High score means your body shape closely tracked the reference movement.',
        'Red or orange heatmap regions show where you deviated most.',
      ],
      lookFor: 'The coloured body-region heatmap on the video.',
    },
  },

  'goals-score': {
    heading: 'Goals Score',
    body: {
      lead: 'How many coach-defined checkpoints ("goals") you passed automatically.',
      points: [
        'Each goal checks one thing: a landmark position, a sequence, or an alignment.',
        'Green tick = passed; red cross = not met during your attempt.',
        'Gate goals (shield icon) are mandatory — failing one zeroes your score.',
      ],
      lookFor: 'The Goals section further down the page.',
    },
  },

  'eom-score': {
    heading: 'Economy of Motion',
    body: {
      lead: 'How efficiently your body travelled through each phase — smooth paths score higher.',
      points: [
        'Measures path smoothness, not technique accuracy.',
        'Hesitation, wobble, or wasted movement lowers this score.',
        'Already factored into your overall score at roughly 15% weight.',
      ],
      lookFor: 'The EOM card in the score breakdown section.',
    },
  },

  'phase-selected': {
    heading: 'Phase Breakdown',
    body: {
      lead: 'Your movement is split into named phases — each scored independently.',
      points: [
        'Click any phase tab to jump both videos to that segment.',
        'A notably lower phase score is your highest-priority area to practise.',
        'Practise the weakest phase in isolation before working the full movement.',
      ],
      lookFor: 'The phase tab bar above the video comparison.',
    },
  },

  'region-selected': {
    heading: 'Body Region Detail',
    body: {
      lead: 'Your coach annotated specific body regions as important for this movement.',
      points: [
        'Critical or High regions have the most influence on your overall score.',
        'Warmer heatmap colours (red/orange) show larger deviation from the reference.',
        'Cooler colours (blue/green) indicate close alignment with the reference.',
      ],
      lookFor: 'The coloured region overlays on the video.',
    },
  },

  'view-mode-2d': {
    heading: '2D Video View',
    body: {
      lead: 'Original camera footage synchronised frame-by-frame with the reference.',
      points: [
        'Toggle body landmarks, region hulls, and skeleton lines with the overlay controls.',
        'Click the performance timeline to jump to any frame.',
        'Use slow motion to examine any moment in detail.',
      ],
      lookFor: 'The video comparison panel with overlay toggles.',
    },
  },

  'view-mode-3d': {
    heading: '3D Skeleton View',
    body: {
      lead: 'Reconstructed skeleton showing depth information not visible in flat video.',
      points: [
        'Useful for seeing torso rotation, arm depth, and forward/back lean.',
        'Most accurate for frontal or diagonal camera angles.',
        'Single-camera reconstruction has limits — use for large differences, not millimetre precision.',
      ],
      lookFor: 'The 3D skeleton toggle above the video panel.',
    },
  },

  'coaching-narrative': {
    heading: 'AI Coaching Feedback',
    body: {
      lead: 'AI-generated feedback specific to your attempt, following a What → How → Why structure.',
      points: [
        'Names the movement pattern that diverged most from the reference.',
        'Describes when in the attempt it happened (e.g. "at 45% of Execution").',
        'Explains the biomechanical reason and gives a correction cue.',
      ],
      lookFor: 'The coaching feedback card below the video section.',
    },
  },

  'video-analysis': {
    heading: 'Video Analysis',
    body: {
      lead: 'Side-by-side comparison of your attempt and the reference video, synchronised frame-by-frame.',
      points: [
        'Performance timeline heat map shows per-frame scores — click any point to jump.',
        'Toggle overlays to show body landmarks, region hulls, or skeleton lines.',
        '3D view (if available) adds depth for frontal or diagonal cameras.',
      ],
      lookFor: 'The dual video panel with the performance timeline above it.',
    },
  },

  'goal-achievements': {
    heading: 'Goal Achievements',
    body: (eval_: EvalSummary): StructuredGuideBody => {
      const hasGoals = eval_.hasGoals ?? true;
      if (!hasGoals) {
        return {
          lead: 'Your coach can define movement checkpoints — called goals — for this template.',
          points: [
            'When goals are set, each is checked automatically and shown here.',
            'Results include a pass/fail indicator and a brief explanation.',
          ],
          lookFor: 'This section will populate once goals are added to the template.',
        };
      }
      return {
        lead: 'Specific checkpoints your coach set — checked automatically against your attempt.',
        points: [
          'Green tick = condition met; red cross = condition not met.',
          'Gate goals (shield icon) are mandatory — failing one zeroes your overall score.',
          'Read the explanation under each goal to understand what to correct.',
        ],
        lookFor: 'The goal cards with pass/fail indicators in this section.',
      };
    },
  },

  // Goal Score (PR-7a) — a NEW, distinct concept from 'goal-achievements'
  // above. The existing entry's own copy calls the six checkpoint types
  // "goals" (pre-dating this feature); this guide card is deliberately
  // worded with "Checks" for those, reserving "Goal" for the new
  // competitive target, per docs/goal_score/F_ui_ux_designs.md §1's
  // naming-collision resolution. Only shown when EvaluationResults.tsx
  // actually renders the Goal Score section (goals[] or total_score
  // present), so this card never appears on a goal-less evaluation.
  'goal-score': {
    heading: 'Goal Score',
    body: {
      lead: 'A bonus on top of your Form score for beating a target your coach set — a Goal, not one of your regular checks.',
      points: [
        'Each Goal tracks one measurement — how far, how fast, or how bent — against a target your coach picked.',
        'Matching the target earns no bonus, same as a regular check; beating it earns bonus points.',
        'Total combines your Goal bonus with your Form score, so it can go above 100 — that\'s expected, not an error.',
      ],
      lookFor: 'The Goal score section, directly under your Form score.',
    },
  },

  'gemini-visual': {
    heading: 'Visual Analysis',
    body: {
      lead: 'A visual AI reviewed frame pairs from your video alongside the reference.',
      points: [
        'Looks for visible form differences not captured in skeleton scoring.',
        'For example: foot placement, weight distribution, or head position.',
        'High-confidence observations are more reliable; treat lower-confidence ones as indicative.',
      ],
      lookFor: 'The visual analysis card with confidence indicators.',
    },
  },

  'movement-analysis': {
    heading: 'Movement Analysis',
    body: {
      lead: 'Measurement-grounded observations computed directly from your pose data — not the visual AI.',
      points: [
        'Flags biomechanical patterns like body-path scooping, lean, or trajectory divergence from the reference.',
        'Every observation is tied to a measured value, so it stays consistent run to run.',
        'Complements the visual analysis above, which looks for what the camera sees rather than what the numbers say.',
      ],
      lookFor: 'The movement analysis block inside the Visual & movement drawer.',
    },
  },

  'technical-diagnostics': {
    heading: 'Technical Diagnostics',
    body: {
      lead: 'The full technical breakdown behind your score — open it when you want the exact numbers.',
      points: [
        'Shows each measured dimension against the tolerance learned from the template\'s own good and bad examples (the calibrated "pricing").',
        'A dimension flagged past its bad-example boundary is what drives a disqualifying (zero) result.',
        'Includes per-goal results, per-phase scores, and economy-of-motion detail — diagnostic, not additional penalties.',
      ],
      lookFor: 'The Technical Diagnostics panel (default closed) at the bottom of the report.',
    },
  },

  'share-card': {
    heading: 'Share Your Score',
    body: {
      lead: 'Download a score card image or copy a direct link to this evaluation.',
      points: [
        'The image includes your score and template name — no personal video is included.',
        'Copy the page URL to share with anyone who has access.',
      ],
      lookFor: 'The share card and copy-link button at the bottom of the page.',
    },
  },

  'comments': {
    heading: 'Coach Comments',
    body: {
      lead: 'Your coach can leave timestamped comments — click a timestamp to jump the video.',
      points: [
        'Reply to start a conversation about specific aspects of your movement.',
        'Comments are private to you and your coach by default.',
        'If shared with multiple coaches, all of them can comment.',
      ],
      lookFor: 'The comments thread at the bottom of the page.',
    },
  },

  'auto-fail': {
    heading: 'Automatic Fail',
    body: {
      lead: 'One or more gate goals were not met — these are mandatory conditions your coach set.',
      points: [
        'Gate goals represent conditions without which the technique cannot score above zero.',
        'For example: performing on the correct side, or a key landmark reaching minimum position.',
        'Fix the gate failure first — passing it unlocks a meaningful score.',
      ],
      lookFor: 'The red gate-fail indicator at the top of your score card.',
    },
  },

  'mirroring-badge': {
    heading: 'Handedness & Mirroring',
    body: (eval_: EvalSummary): StructuredGuideBody => {
      if (eval_.mirrored) {
        return {
          lead: 'The AI detected you performed on the opposite side — your video was mirrored before scoring.',
          points: [
            'Mirroring ensures the comparison is fair regardless of which side you lead with.',
            'If this is incorrect, ask your coach to set your handedness in your athlete profile.',
            'Declared handedness always overrides automatic detection.',
          ],
          lookFor: 'The handedness badge near your overall score.',
        };
      }
      return {
        lead: 'Your handedness setting controls whether your video is mirrored before scoring.',
        points: [
          '"Declared" badge = your coach or profile specified your lead side explicitly.',
          '"Inferred" badge = the AI detected your lead side automatically from the video.',
          'Declare it in your profile for more consistent results.',
        ],
        lookFor: 'The handedness badge near your overall score.',
      };
    },
  },

  default: {
    heading: 'Adaptive Guide',
    body:
      'This panel explains what you\'re looking at as you explore your evaluation. ' +
      'Scroll down the page and the guide will update to explain each section. ' +
      'Tap a phase tab to learn what that phase measures. ' +
      'You can dismiss this guide at any time by pressing the × button, ' +
      'or retract it to a handle and expand it again whenever you need it.',
  },
};

// ---------------------------------------------------------------------------
// Section → Key map (B-2026-0086)
// ---------------------------------------------------------------------------

/**
 * Maps `data-guide-section` attribute values (used in EvaluationResults.tsx)
 * to registry keys. The scroll-spy hook uses this to drive the overlay.
 *
 * Every key in this map must exist in EVAL_GUIDE_CONTENT; every registry
 * entry must be reachable via at least one path (section, tap signal, or
 * default).
 */
export const SECTION_TO_KEY: Record<string, EvalGuideKey> = {
  'hero-overall':       'overall-score',
  'eom-card':           'eom-score',
  'body-form-card':     'body-form-score',
  'video-analysis':     'video-analysis',
  'phase-breakdown':    'phase-selected',
  'goal-achievements':  'goal-achievements',
  'goal-score':         'goal-score',
  'gemini-visual':      'gemini-visual',
  // Slice-1e "Drill-In Ledger" drawer sections (were falling through to the
  // generic default card — B-2026-0127 audit §3B). economy-of-motion reuses the
  // existing EOM entry; movement-analysis + technical-diagnostics are new.
  'economy-of-motion':  'eom-score',
  'movement-analysis':  'movement-analysis',
  'technical-diagnostics': 'technical-diagnostics',
  'coaching-narrative': 'coaching-narrative',
  'share-card':         'share-card',
  'comments':           'comments',
};

// ---------------------------------------------------------------------------
// Resolver helpers
// ---------------------------------------------------------------------------

/**
 * Resolve guide content for the current context key.
 * Returns a specific entry if available, or the default entry as a fallback.
 */
export function resolveGuideContent(key: string | null | undefined): GuideEntry {
  if (key && key in EVAL_GUIDE_CONTENT) {
    return EVAL_GUIDE_CONTENT[key as EvalGuideKey];
  }
  return EVAL_GUIDE_CONTENT.default;
}
